import { forwardRef } from 'react';
import {
  Link as ReactRouterLink,
  LinkProps as ReactRouterLinkProps,
} from 'react-router';

import {
  chakra,
  LinkProps as ChakraLinkProps,
  HTMLChakraProps,
  omitThemingProps,
  ThemingProps,
  useStyleConfig,
} from '@chakra-ui/react';

import { EXTERNAL_LINK_DISCLAIMER_MODAL_ID } from '@/constant/modals';
import { useModal } from '@/hooks/useModal';
import { cn } from '@/lib/utils';
import { glassyEffectModal } from '@/styles/variants/glassy';

import { Disclaimer } from '../Disclaimer';

export type LinkProps = HTMLChakraProps<'a'> &
  ThemingProps<'Link'> &
  ChakraLinkProps &
  ReactRouterLinkProps;

// External: https URL that doesn't contain 'vh3'
const EXTERNAL_LINK_REGEX = /^https?:\/\/(?!.*vh3).+/i;
const isExternal = (url: string): boolean => EXTERNAL_LINK_REGEX.test(url);

export const Link = forwardRef<HTMLAnchorElement, LinkProps>(
  function Link(props, ref) {
    const styles = useStyleConfig('Link', props);
    const { className, children, to, href, ...rest } = omitThemingProps(
      props
    ) as LinkProps;

    const { openModal, closeModal } = useModal();
    const destination = (typeof to === 'string' ? to : href) ?? '#';

    const openExternalDisclaimer = (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      const url = destination;

      const handleAccept = () => {
        window.open(url, '_blank', 'noopener,noreferrer');
        closeModal(EXTERNAL_LINK_DISCLAIMER_MODAL_ID);
      };

      openModal({
        id: EXTERNAL_LINK_DISCLAIMER_MODAL_ID,
        title: '',
        body: (
          <Disclaimer
            url={url}
            onAccept={handleAccept}
            onCancel={() => closeModal(EXTERNAL_LINK_DISCLAIMER_MODAL_ID)}
          />
        ),
        size: 'md',
        hideCloseButton: true,
        hideCustomCloseButton: true,
        isCentered: true,
        closeOnEsc: true,
        closeOnOverlayClick: true,
        headerProps: { display: 'none' },
        contentProps: {
          rounded: '16px',
          overflow: 'hidden',
          sx: {
            ...glassyEffectModal,
            '& > div': {
              p: 0,
            },
          },
        },
        overlayProps: { bg: 'blackAlpha.500' },
        onClose: () => closeModal(EXTERNAL_LINK_DISCLAIMER_MODAL_ID),
      });
    };

    if (!isExternal(destination)) {
      return (
        <chakra.a
          ref={ref}
          as={ReactRouterLink}
          to={to}
          className={cn('chakra-link', className)}
          __css={styles}
          {...rest}
        >
          {children}
        </chakra.a>
      );
    }

    return (
      <chakra.a
        ref={ref}
        href={destination}
        target='_blank'
        rel='noopener noreferrer'
        className={cn('chakra-link', className)}
        __css={styles}
        onClick={openExternalDisclaimer}
        {...rest}
      >
        {children}
      </chakra.a>
    );
  }
);
